import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import Logo from "../components/app/Logo";
import { Sparkles, ArrowRight, CheckCircle2, AlertCircle, UserPlus, LogIn } from "lucide-react";

/**
 * ClaimInvite — public-then-authenticated flow for player profile claims.
 *
 *  1. Fetches /api/invite/:token to display the inviter + player snapshot.
 *  2. If the user is NOT logged in → shows two CTAs:
 *       - "Create player account" (prefilled email, role=player)
 *       - "I already have an account" (login then auto-redirect back here)
 *  3. If the user IS logged in:
 *       - role=player → POST /api/players/claim with the token → redirect to /.
 *       - other role  → friendly message: switch accounts.
 */
export default function ClaimInvite() {
  const { token } = useParams();
  const { user, register, login } = useAuth();
  const nav = useNavigate();
  const { lang } = useI18n();

  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("signup"); // signup | login
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invite/${token}`);
        setInvite(data);
        setForm((p) => ({ ...p, email: data.email || "", name: data.name || "" }));
      } catch (e) {
        setErr(e?.response?.data?.detail || "Invite not found");
      }
    })();
  }, [token]);

  // Already authenticated → attempt claim right away (player only)
  useEffect(() => {
    if (!user || !invite || claimed || busy) return;
    if (user.role !== "player") return; // wrong role — show notice instead
    (async () => {
      setBusy(true);
      try {
        await api.post("/players/claim", { invite_token: token });
        setClaimed(true);
        setTimeout(() => nav("/"), 1400);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Claim failed");
      }
      setBusy(false);
    })();
  }, [user, invite, claimed, busy, token, nav]);

  const submitSignup = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await register({ ...form, role: "player" });
      // useEffect above will auto-claim once user is set
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message);
      setBusy(false);
    }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(form.email, form.password);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message);
      setBusy(false);
    }
  };

  if (err && !invite) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="card-sharp p-8 max-w-md w-full text-center rounded-lg" data-testid="claim-invalid">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-300" />
          <h1 className="font-oswald uppercase text-2xl tracking-tight mb-2">
            {lang === "bg" ? "Поканата е невалидна" : "Invite invalid"}
          </h1>
          <p className="text-sm text-white/55 mb-4">{err}</p>
          <Link to="/" className="btn-primary px-4 py-2 inline-flex items-center gap-2">
            <ArrowRight className="w-4 h-4" />
            {lang === "bg" ? "Към началото" : "Go home"}
          </Link>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center" data-testid="claim-loading">
        <div className="pitch-loader"><div className="ball" /></div>
      </div>
    );
  }

  if (claimed) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="card-sharp p-8 max-w-md w-full text-center rounded-lg" data-testid="claim-success">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-pitch/15 border border-pitch flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-pitch" />
          </div>
          <h1 className="font-oswald uppercase text-2xl tracking-tight mb-2">
            {lang === "bg" ? "Профилът е свързан!" : "Profile linked!"}
          </h1>
          <p className="text-sm text-white/55">
            {lang === "bg" ? "Пренасочване към таблото..." : "Redirecting to your dashboard..."}
          </p>
        </div>
      </div>
    );
  }

  if (user && user.role !== "player") {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="card-sharp p-8 max-w-md w-full text-center rounded-lg" data-testid="claim-wrong-role">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-300" />
          <h1 className="font-oswald uppercase text-xl tracking-tight mb-2">
            {lang === "bg" ? "Невалиден тип акаунт" : "Wrong account type"}
          </h1>
          <p className="text-sm text-white/55 mb-4">
            {lang === "bg"
              ? `Тази покана е за играч. Влязъл си като ${user.role}. Излез и регистрирай нов player акаунт.`
              : `This invite is for a player. You're logged in as ${user.role}. Sign out and create a player account.`}
          </p>
          <Link to="/" className="btn-secondary px-4 py-2 text-sm">{lang === "bg" ? "Към началото" : "Home"}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-4 sm:p-6" data-testid="claim-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-6">
          <Logo size={32} wordmark />
        </Link>

        {/* Invite summary */}
        <div className="card-sharp p-6 mb-4 rounded-lg" data-testid="claim-invite-summary">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-pitch" />
            <span className="label-mono text-pitch">// {lang === "bg" ? "Покана за играч" : "Player invite"}</span>
          </div>
          <h1 className="font-oswald uppercase text-2xl sm:text-3xl tracking-tight mb-1">
            {lang === "bg" ? "Здравей, " : "Hi, "}<span className="text-pitch">{invite.name}!</span>
          </h1>
          <p className="text-sm text-white/55 leading-relaxed">
            {invite.inviter_name
              ? (lang === "bg"
                  ? `${invite.inviter_name} те покани да създадеш свой собствен профил в ONTHEPITCH.`
                  : `${invite.inviter_name} invited you to claim your own ONTHEPITCH profile.`)
              : (lang === "bg"
                  ? "Покани са те да създадеш свой собствен профил в ONTHEPITCH."
                  : "You've been invited to claim your own ONTHEPITCH profile.")}
          </p>
          <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/8">
            {invite.age != null && (
              <div className="text-center">
                <div className="font-oswald text-2xl text-pitch">{invite.age}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">{lang === "bg" ? "Възраст" : "Age"}</div>
              </div>
            )}
            {invite.position && (
              <div className="text-center">
                <div className="font-oswald text-sm text-pitch truncate">{invite.position}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">{lang === "bg" ? "Позиция" : "Position"}</div>
              </div>
            )}
            <div className="text-center">
              <div className="font-oswald text-xs text-pitch truncate">{invite.email}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">Email</div>
            </div>
          </div>
        </div>

        {/* Mode tabs */}
        <div className="flex items-center gap-1 mb-3 bg-white/[0.03] p-1 rounded-md">
          <button
            type="button"
            onClick={() => { setMode("signup"); setErr(""); }}
            className={`flex-1 py-2 px-3 text-xs font-mono uppercase tracking-widest rounded transition-colors flex items-center justify-center gap-1.5 ${mode === "signup" ? "bg-pitch text-white" : "text-white/55 hover:text-white"}`}
            data-testid="claim-mode-signup"
          >
            <UserPlus className="w-3 h-3" /> {lang === "bg" ? "Регистрация" : "Sign up"}
          </button>
          <button
            type="button"
            onClick={() => { setMode("login"); setErr(""); }}
            className={`flex-1 py-2 px-3 text-xs font-mono uppercase tracking-widest rounded transition-colors flex items-center justify-center gap-1.5 ${mode === "login" ? "bg-pitch text-white" : "text-white/55 hover:text-white"}`}
            data-testid="claim-mode-login"
          >
            <LogIn className="w-3 h-3" /> {lang === "bg" ? "Имам акаунт" : "I have one"}
          </button>
        </div>

        <form onSubmit={mode === "signup" ? submitSignup : submitLogin} className="card-sharp p-5 sm:p-6 space-y-3 rounded-lg">
          {mode === "signup" && (
            <div>
              <label className="label-mono block mb-1.5">{lang === "bg" ? "Име" : "Name"}</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none rounded-md"
                data-testid="claim-name"
              />
            </div>
          )}
          <div>
            <label className="label-mono block mb-1.5">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              disabled={mode === "signup"}
              className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none rounded-md disabled:opacity-70"
              data-testid="claim-email"
            />
            {mode === "signup" && (
              <div className="text-[10px] font-mono text-white/35 mt-1">
                {lang === "bg" ? "Този email е фиксиран от поканата" : "This email is fixed by the invite"}
              </div>
            )}
          </div>
          <div>
            <label className="label-mono block mb-1.5">{lang === "bg" ? "Парола" : "Password"}</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none rounded-md"
              data-testid="claim-password"
            />
          </div>
          {err && <div className="text-red-400 text-xs font-mono" data-testid="claim-error">{err}</div>}
          <button type="submit" disabled={busy} className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="claim-submit">
            {busy ? "..." : (mode === "signup"
              ? (lang === "bg" ? "Създай и свържи профила" : "Create & link account")
              : (lang === "bg" ? "Вход и свържи" : "Log in & link"))}
          </button>
        </form>
      </div>
    </div>
  );
}
